var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import classNames from 'classnames';
import { useUXPContext } from '@core/context/useUXPContext';
import { FontAwesomeIconWrapper } from '@icon/fontawesome/FontAwesomeIconWrapper';
import HorizontalScrollList from '@widget/list/HorizontalScrollList';
import FileInput from '../FileInput/FileInput';
import MediaPickerModal from './MediaPickerModal';
import MediaStripCard from './MediaStripCard';
import UploadStatus from './UploadStatus';
import { addUnique, resolveAllowedTypes, runFileCheck, useLibraryUpload } from './mediaLibrary';
import { getUploadSizeLimitMB } from '@hooks/useFileUpload';
/**
 * @export
 * A media field — image, icon, document, video or audio — backed by the user's
 * media library, the platform image gallery, font icons, uploads and plain URLs.
 *
 * @example
 * ```tsx
 * <MediaPicker
 *     mediaTypes={['Image']}
 *     value={imageUrl}
 *     onChange={url => setImageUrl(url as string)}
 * />
 * ```
 *
 * @category Inputs/Pickers/Media
 */
const MediaPicker = (props) => {
    const { mediaTypes, value, onChange, multiple, allowedTypes, sources, defaultSource, variant = 'compact', saveToLibrary, uploadPath, placeholder, className, searchParameters, minWidth, maxWidth, width, fullWidth, height, minHeight, maxHeight, disabled, readOnly, isValid, onUpload, onPick, validate, onRefreshRules, cropRules, } = props;
    const context = useUXPContext();
    // A file dropped or chosen on the field itself goes through the host's onUpload too
    const fileUploader = React.useMemo(() => onUpload ? (f) => onUpload(f, 'upload') : undefined, [onUpload]);
    const { uploadToLibrary, uploadAllToLibrary, isUploading, progress, uploadingIndex, uploadingTotal } = useLibraryUpload({ uploadPath, saveToLibrary, uploader: fileUploader });
    const [browsing, setBrowsing] = React.useState(false);
    const [uploadError, setUploadError] = React.useState(null);
    const values = Array.isArray(value) ? value : (value ? [value] : []);
    const singleValue = Array.isArray(value) ? (value[0] || '') : (value || '');
    const effectiveAllowedTypes = allowedTypes !== null && allowedTypes !== void 0 ? allowedTypes : resolveAllowedTypes(mediaTypes);
    function openBrowser() {
        if (!disabled && !readOnly)
            setBrowsing(true);
    }
    function emit(url) {
        if (multiple)
            onChange(addUnique(values, [url]));
        else
            onChange(url);
    }
    function emitMany(urls) {
        onChange(addUnique(values, urls));
    }
    function removeValue(url) {
        onChange(values.filter(v => v !== url));
    }
    // FileInput reports a rejection as one boolean, so work out which rule the
    // file broke to tell the user something useful.
    function rejectionMessage(file) {
        const maxSizeMB = getUploadSizeLimitMB(context);
        if (maxSizeMB > 0 && file && file.size > maxSizeMB * 1024 * 1024) {
            return context.$L('uxp-core.form-error.file-too-large', { maxSizeMB });
        }
        return context.$L('uxp-core.form-error.invalid-file');
    }
    function onFileSelected(file, fileIsValid) {
        return __awaiter(this, void 0, void 0, function* () {
            // FileInput signals its clear button by passing no file. Only the single
            // variant renders one — the multiple variant gives FileInput no value to
            // preview, so it has nothing to clear.
            if (!file) {
                setUploadError(null);
                if (!multiple)
                    onChange('');
                return;
            }
            if (!fileIsValid) {
                setUploadError(rejectionMessage(file));
                return;
            }
            // The field's check first: a refused file is not uploaded
            const problem = yield runFileCheck(validate, file);
            if (problem) {
                setUploadError(problem.split('\n').join(' '));
                return;
            }
            setUploadError(null);
            try {
                emit(yield uploadToLibrary(file));
            }
            catch (e) {
                // The reason the upload gave, when it gave one
                setUploadError((e === null || e === void 0 ? void 0 : e.message) || context.$L('uxp-core.media-picker.upload-failed'));
            }
        });
    }
    // Multi-select fields upload the whole pick in one go; the ones that fail
    // are reported without costing the user the ones that worked.
    function onFilesSelected(files, filesAreValid) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!files.length)
                return;
            if (!filesAreValid) {
                setUploadError(rejectionMessage(files.find(f => !f || f.size > getUploadSizeLimitMB(context) * 1024 * 1024)));
                return;
            }
            setUploadError(null);
            // The field's check first: refused files are left out, with their reasons
            const refused = [];
            const accepted = [];
            for (const file of files) {
                const problem = yield runFileCheck(validate, file);
                if (problem)
                    refused.push({ file, message: problem.split('\n').join(' ') });
                else
                    accepted.push(file);
            }
            const { urls, errors } = accepted.length
                ? yield uploadAllToLibrary(accepted)
                : { urls: [], errors: [] };
            if (urls.length)
                emitMany(urls);
            const failures = [...refused, ...errors];
            if (failures.length) {
                // The count, then each file with its reason
                const reasons = failures.map(f => f.message ? `${f.file.name}: ${f.message}` : f.file.name).join('; ');
                setUploadError(`${context.$L('uxp-core.media-picker.upload-failed-count', { count: failures.length })}. ${reasons}`);
            }
        });
    }
    const modal = _jsx(MediaPickerModal, { show: browsing, onClose: () => setBrowsing(false), onSelect: (v) => onChange(multiple ? [...values, v] : v), multiple: multiple, initialSelection: values, onSelectMultiple: (vals) => onChange(vals), mediaTypes: mediaTypes, sources: sources, defaultSource: defaultSource, allowedTypes: effectiveAllowedTypes, saveToLibrary: saveToLibrary, uploadPath: uploadPath, searchParameters: searchParameters, onUpload: onUpload, onPick: onPick, validate: validate, onRefreshRules: onRefreshRules, cropRules: cropRules });
    const compact = variant === 'compact';
    // Compact hosts the browse affordance inside the field; the drop-zone has
    // no field to put it in, so it gets a link underneath instead. Clicking a
    // compact field opens the browser rather than the OS picker — the dialog's
    // own Upload tab is the way in, so two competing file dialogs would only
    // confuse. Dropping a file on the field still uploads it directly.
    const browseSuffix = _jsx("span", { className: "uxpcore_mediapicker__browse", title: context.$L('uxp-core.media-picker.browse-library'), onClick: openBrowser, children: _jsx(FontAwesomeIconWrapper, { icon: ['fal', 'folder-open'] }) });
    const fileInput = _jsx(FileInput, { value: multiple ? undefined : singleValue, onChange: onFileSelected, multiple: multiple, onFilesChange: onFilesSelected, allowedTypes: effectiveAllowedTypes, maxSizeMB: getUploadSizeLimitMB(context), compact: compact, suffix: compact ? browseSuffix : undefined, hidePrefixIcon: compact, onFieldClick: compact ? openBrowser : undefined, dropAreaLabel: placeholder, disabled: disabled || isUploading, readOnly: readOnly, isValid: isValid, minWidth: minWidth, maxWidth: maxWidth, width: width, fullWidth: fullWidth !== null && fullWidth !== void 0 ? fullWidth : true, height: height, minHeight: minHeight, maxHeight: maxHeight });
    return _jsxs("div", { className: classNames('uxpcore_mediapicker', `uxpcore_mediapicker--${variant}`, className), children: [fileInput, !compact &&
                _jsx("span", { className: "uxpcore_mediapicker__browse-link", onClick: openBrowser, children: context.$L('uxp-core.media-picker.browse-library') }), multiple && values.length > 0 &&
                _jsx("div", { className: "uxpcore_mediapicker__strip", children: _jsx(HorizontalScrollList, { items: values, renderItem: (url) => _jsx(MediaStripCard, { value: url, onRemove: !disabled && !readOnly ? () => removeValue(url) : undefined, removeTitle: context.$L('uxp-core.media-picker.remove-item') }) }) }), _jsx(UploadStatus, { isUploading: isUploading, progress: progress, uploadingIndex: uploadingIndex, uploadingTotal: uploadingTotal, error: uploadError }), modal] });
};
export default MediaPicker;
